import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import {
  BRANDS,
  CREATE_BRAND,
  PLATFORM_SETTINGS,
  SET_BRAND_ADMIN,
  STAY_SERVICE_FEE_RATES,
  UPDATE_BRAND_ALIASES,
  UPDATE_BRAND_PRODUCT_TYPES,
  UPDATE_BRAND_STRIPE_SECRETS,
  UPDATE_PLATFORM_REFERRAL_RATES,
  UPDATE_STAY_SERVICE_FEE_RATES,
} from '../../services/brandService/queries';
import {
  BRAND_PRODUCT_LABELS,
  brandProductOf,
  parseFeePercent,
  productTypesInput,
  type BrandProduct,
} from './brandProducts';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
  Field,
  FormGrid,
  Input,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Muted,
  SectionHeader,
  Select,
  Stack,
} from '../../components/ui';

const BrandMeta = styled.div`
  ${tw`flex flex-wrap items-center gap-2 mt-1`}
`;

const SubSections = styled.div`
  ${tw`px-5`}
`;

const SubSection = styled.div`
  ${tw`py-5`}
  & + & {
    border-top: 1px solid var(--color-border);
  }
`;

const SubTitle = styled.h4`
  ${tw`text-sm font-semibold mb-3`}
  color: var(--color-text-primary);
`;

const InlineActions = styled.div`
  ${tw`flex flex-wrap items-center gap-2 mt-3`}
`;

type SecretDraft = { secretKey: string; webhookSecret: string; publishableKey: string };
const emptySecrets: SecretDraft = { secretKey: '', webhookSecret: '', publishableKey: '' };

function parseList(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map(v => v.trim())
    .filter(Boolean);
}

/**
 * Platform owner only (D1, D4, D12, D19, D25): the brands, their aliases and
 * Stripe secrets, who is a brand admin, and the platform referral rates.
 * Spec 016 O4: what each brand sells (cars or places to stay) and the stays
 * service fee percents.
 */
export default function AdminBrandsPage() {
  const { data, loading, error, refetch } = useQuery(BRANDS, { fetchPolicy: 'network-only' });
  const { data: settingsData } = useQuery(PLATFORM_SETTINGS, { fetchPolicy: 'network-only' });
  const [createBrand, { loading: creating }] = useMutation(CREATE_BRAND);
  const [updateAliases] = useMutation(UPDATE_BRAND_ALIASES);
  const [updateSecrets] = useMutation(UPDATE_BRAND_STRIPE_SECRETS);
  const [setBrandAdmin] = useMutation(SET_BRAND_ADMIN);
  const [updateRates, { loading: savingRates }] = useMutation(UPDATE_PLATFORM_REFERRAL_RATES, {
    refetchQueries: [{ query: PLATFORM_SETTINGS }],
  });
  const [updateProductTypes] = useMutation(UPDATE_BRAND_PRODUCT_TYPES);
  const { data: feeData } = useQuery(STAY_SERVICE_FEE_RATES, { fetchPolicy: 'network-only' });
  const [updateFees, { loading: savingFees }] = useMutation(UPDATE_STAY_SERVICE_FEE_RATES, {
    refetchQueries: [{ query: STAY_SERVICE_FEE_RATES }],
  });

  const [newBrandSite, setNewBrandSite] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newAliases, setNewAliases] = useState('');
  const [newSupportEmail, setNewSupportEmail] = useState('');
  const [newProduct, setNewProduct] = useState<BrandProduct>('cars');
  const [productDrafts, setProductDrafts] = useState<Record<string, BrandProduct>>({});
  const [guestFee, setGuestFee] = useState('');
  const [hostFee, setHostFee] = useState('');
  const [defaultRate, setDefaultRate] = useState('');
  const [minimumRate, setMinimumRate] = useState('');
  const [aliasDrafts, setAliasDrafts] = useState<Record<string, string>>({});
  const [secretDrafts, setSecretDrafts] = useState<Record<string, SecretDraft>>({});
  const [adminDrafts, setAdminDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    const settings = settingsData?.platformSettings;
    if (!settings) return;
    setDefaultRate(String(settings.defaultReferralCommissionRate ?? ''));
    setMinimumRate(String(settings.minimumReferralCommissionRate ?? ''));
  }, [settingsData?.platformSettings]);

  useEffect(() => {
    const rates = feeData?.stayServiceFeeRates;
    if (!rates) return;
    setGuestFee(String(rates.guestServiceFeeRate ?? ''));
    setHostFee(String(rates.hostServiceFeeRate ?? ''));
  }, [feeData?.stayServiceFeeRates]);

  useEffect(() => {
    const brands: any[] = data?.brands ?? [];
    setAliasDrafts(prev => {
      const next = { ...prev };
      for (const brand of brands) {
        if (next[brand.brandSite] === undefined) {
          next[brand.brandSite] = (brand.aliases ?? []).join(', ');
        }
      }
      return next;
    });
  }, [data?.brands]);

  const handleCreate = async () => {
    try {
      await createBrand({
        variables: {
          input: {
            brandSite: newBrandSite.trim(),
            displayName: newDisplayName.trim() || null,
            aliases: parseList(newAliases),
            ...(newSupportEmail.trim() ? { supportEmail: newSupportEmail.trim() } : {}),
            ...productTypesInput(newProduct),
          },
        },
      });
      toast.success(`Brand ${newBrandSite.trim()} created`);
      setNewBrandSite('');
      setNewDisplayName('');
      setNewAliases('');
      setNewSupportEmail('');
      setNewProduct('cars');
      await refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create brand');
    }
  };

  const saveRates = async () => {
    try {
      await updateRates({
        variables: {
          input: {
            defaultReferralCommissionRate: Number(defaultRate),
            minimumReferralCommissionRate: Number(minimumRate),
          },
        },
      });
      toast.success('Platform referral rates saved');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save rates');
    }
  };

  const saveFees = async () => {
    const guest = parseFeePercent(guestFee);
    const host = parseFeePercent(hostFee);
    if (guest === null || host === null) {
      toast.error('Fees are percents from 0 to 30');
      return;
    }
    try {
      await updateFees({
        variables: { input: { stayGuestServiceFeeRate: guest, stayHostServiceFeeRate: host } },
      });
      toast.success('Stays service fees saved');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save fees');
    }
  };

  const saveProduct = async (brandSite: string, product: BrandProduct) => {
    try {
      await updateProductTypes({
        variables: { input: { brandSite, ...productTypesInput(product) } },
      });
      toast.success(`${brandSite} now sells ${BRAND_PRODUCT_LABELS[product].toLowerCase()}`);
      await refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save what the brand sells');
    }
  };

  const saveAliases = async (brandSite: string) => {
    try {
      await updateAliases({
        variables: { input: { brandSite, aliases: parseList(aliasDrafts[brandSite] ?? '') } },
      });
      toast.success('Aliases saved');
      await refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save aliases');
    }
  };

  const saveAdmin = async (brandSite: string, isAdmin: boolean) => {
    try {
      await setBrandAdmin({
        variables: {
          input: { userId: (adminDrafts[brandSite] ?? '').trim(), brandSite, isAdmin },
        },
      });
      toast.success(isAdmin ? 'Brand admin appointed' : 'Brand admin removed');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update admin');
    }
  };

  const saveSecrets = async (brandSite: string) => {
    const secrets = secretDrafts[brandSite] ?? emptySecrets;
    try {
      await updateSecrets({
        variables: {
          input: {
            brandSite,
            ...(secrets.secretKey.trim() ? { stripeSecretKey: secrets.secretKey.trim() } : {}),
            ...(secrets.webhookSecret.trim()
              ? { stripeWebhookSecret: secrets.webhookSecret.trim() }
              : {}),
            ...(secrets.publishableKey.trim()
              ? { stripePublishableKey: secrets.publishableKey.trim() }
              : {}),
          },
        },
      });
      toast.success(`Stripe secrets saved. Webhook path: /stripe/webhook/${brandSite}`);
      setSecretDrafts(d => ({ ...d, [brandSite]: emptySecrets }));
      await refetch();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save Stripe secrets');
    }
  };

  const setSecret = (brandSite: string, patch: Partial<SecretDraft>) =>
    setSecretDrafts(d => ({ ...d, [brandSite]: { ...(d[brandSite] ?? emptySecrets), ...patch } }));

  const brands: any[] = data?.brands ?? [];

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Platform owner"
        title="Brands"
        description="A brand is a production domain with its own site, apps, admins and Stripe account. Aliases such as localhost:3080 map to it. Brand admins edit their own search settings and referral percent on their brand's site."
      />

      <Stack>
        <Card>
          <CardHeader
            title="Platform referral rates"
            description="A checkout brand with no rate of its own pays the default. No brand may set a rate below the minimum; the locked rate is max(brand rate or default, minimum)."
          />
          <CardBody>
            <FormGrid>
              <Field label="Default percent" htmlFor="platform-default-rate">
                <Input
                  id="platform-default-rate"
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={defaultRate}
                  onChange={e => setDefaultRate(e.target.value)}
                />
              </Field>
              <Field label="Minimum percent" htmlFor="platform-minimum-rate">
                <Input
                  id="platform-minimum-rate"
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={minimumRate}
                  onChange={e => setMinimumRate(e.target.value)}
                />
              </Field>
            </FormGrid>
          </CardBody>
          <CardFooter>
            <Button
              text="Save rates"
              type="button"
              size="sm"
              isLoading={savingRates}
              onClick={saveRates}
            />
          </CardFooter>
        </Card>

        <Card>
          <CardHeader
            title="Places to stay: service fees"
            description="Applies to every brand that sells places to stay (spec 016). The guest fee is added to the guest's total; the host fee comes out of the host's payout. Existing bookings keep the rates they were priced with."
          />
          <CardBody>
            <FormGrid>
              <Field label="Guest service fee (percent)" htmlFor="stay-guest-fee">
                <Input
                  id="stay-guest-fee"
                  type="number"
                  min={0}
                  max={30}
                  step={0.5}
                  value={guestFee}
                  onChange={e => setGuestFee(e.target.value)}
                />
              </Field>
              <Field label="Host service fee (percent)" htmlFor="stay-host-fee">
                <Input
                  id="stay-host-fee"
                  type="number"
                  min={0}
                  max={30}
                  step={0.5}
                  value={hostFee}
                  onChange={e => setHostFee(e.target.value)}
                />
              </Field>
            </FormGrid>
          </CardBody>
          <CardFooter>
            <Button
              text="Save fees"
              type="button"
              size="sm"
              isLoading={savingFees}
              onClick={saveFees}
              dataTestId="save-stay-fees"
            />
          </CardFooter>
        </Card>

        <Card>
          <CardHeader
            title="Create a brand"
            description="Create the brand only after enforcement is on. Then appoint its admin below; the admin enables airports and sets the brand's own settings on its site."
          />
          <CardBody>
            <FormGrid $cols={3}>
              <Field label="Production domain" htmlFor="new-brand-site" required>
                <Input
                  id="new-brand-site"
                  placeholder="lenvoy.co"
                  value={newBrandSite}
                  onChange={e => setNewBrandSite(e.target.value)}
                />
              </Field>
              <Field label="Display name" htmlFor="new-brand-name">
                <Input
                  id="new-brand-name"
                  placeholder="Lenvoy Car Rental"
                  value={newDisplayName}
                  onChange={e => setNewDisplayName(e.target.value)}
                />
              </Field>
              <Field label="Aliases" htmlFor="new-brand-aliases" hint="Comma separated hosts.">
                <Input
                  id="new-brand-aliases"
                  placeholder="www.lenvoy.co, localhost:3080"
                  value={newAliases}
                  onChange={e => setNewAliases(e.target.value)}
                />
              </Field>
              <Field label="Support email" htmlFor="new-brand-support">
                <Input
                  id="new-brand-support"
                  type="email"
                  placeholder="support@lenvoy.co"
                  value={newSupportEmail}
                  onChange={e => setNewSupportEmail(e.target.value)}
                />
              </Field>
              <Field
                label="Sells"
                htmlFor="new-brand-product"
                hint="Places to stay needs the stays build (brandId stays) on this domain."
              >
                <Select
                  id="new-brand-product"
                  value={newProduct}
                  onChange={e => setNewProduct(e.target.value as BrandProduct)}
                >
                  <option value="cars">{BRAND_PRODUCT_LABELS.cars}</option>
                  <option value="stays">{BRAND_PRODUCT_LABELS.stays}</option>
                </Select>
              </Field>
            </FormGrid>
          </CardBody>
          <CardFooter>
            <Button
              text="Create brand"
              type="button"
              size="sm"
              isLoading={creating}
              isDisabled={!newBrandSite.trim()}
              onClick={handleCreate}
            />
          </CardFooter>
        </Card>

        <SectionHeader
          title="Existing brands"
          description={`${brands.length} brand${brands.length === 1 ? '' : 's'} on this platform.`}
        />
        {loading && <LoadingBlock label="Loading brands" />}
        {error && (
          <Alert tone="error" title="Could not load brands">
            {error.message}
          </Alert>
        )}
        {!loading && !error && brands.length === 0 && (
          <Card>
            <EmptyState icon={faLayerGroup} title="No brands yet" />
          </Card>
        )}
        {brands.map(brand => {
          const secrets = secretDrafts[brand.brandSite] ?? emptySecrets;
          const adminDraft = (adminDrafts[brand.brandSite] ?? '').trim();
          const product = brandProductOf(brand);
          const productDraft = productDrafts[brand.brandSite] ?? product;
          return (
            <Card key={brand.id} data-testid="brand-row">
              <CardHeader
                level={3}
                title={brand.displayName}
                description={
                  <BrandMeta>
                    <Badge tone="brand">{brand.brandSite}</Badge>
                    <Badge tone="neutral">
                      Referral{' '}
                      {brand.referralCommissionRate === null ||
                      brand.referralCommissionRate === undefined
                        ? 'platform default'
                        : `${brand.referralCommissionRate}%`}
                    </Badge>
                    <Badge tone={brand.showOtherSites ? 'info' : 'neutral'}>
                      {brand.showOtherSites ? 'Shows other brands' : 'Own cars only'}
                    </Badge>
                    <Badge tone={product === 'stays' ? 'info' : 'neutral'}>
                      {BRAND_PRODUCT_LABELS[product]}
                    </Badge>
                    <Badge tone={brand.stripePublishableKey ? 'success' : 'neutral'} dot>
                      Stripe {brand.stripePublishableKey ? 'own account' : 'platform account'}
                    </Badge>
                  </BrandMeta>
                }
              />
              <CardBody $padding="none">
                <SubSections>
                  <SubSection>
                    <SubTitle>Sells</SubTitle>
                    <Field hint="Places to stay sets the default and allowed product type to house; cars clears the list (every type except house).">
                      <Select
                        value={productDraft}
                        data-testid="brand-product-select"
                        onChange={e =>
                          setProductDrafts(d => ({
                            ...d,
                            [brand.brandSite]: e.target.value as BrandProduct,
                          }))
                        }
                      >
                        <option value="cars">{BRAND_PRODUCT_LABELS.cars}</option>
                        <option value="stays">{BRAND_PRODUCT_LABELS.stays}</option>
                      </Select>
                    </Field>
                    <InlineActions>
                      <Button
                        text="Save"
                        theme="secondary"
                        size="sm"
                        type="button"
                        isDisabled={productDraft === product}
                        onClick={() => saveProduct(brand.brandSite, productDraft)}
                      />
                    </InlineActions>
                  </SubSection>

                  <SubSection>
                    <SubTitle>Aliases</SubTitle>
                    <Field hint="Every host that must resolve to this brand, comma separated. Saving replaces the whole list.">
                      <Input
                        value={aliasDrafts[brand.brandSite] ?? ''}
                        onChange={e =>
                          setAliasDrafts(d => ({ ...d, [brand.brandSite]: e.target.value }))
                        }
                      />
                    </Field>
                    <InlineActions>
                      <Button
                        text="Save aliases"
                        theme="secondary"
                        size="sm"
                        type="button"
                        onClick={() => saveAliases(brand.brandSite)}
                      />
                    </InlineActions>
                  </SubSection>

                  <SubSection>
                    <SubTitle>Brand admin</SubTitle>
                    <Field label="User id" hint="Appoint or remove a site admin by their user id.">
                      <Input
                        placeholder="user uuid"
                        value={adminDrafts[brand.brandSite] ?? ''}
                        onChange={e =>
                          setAdminDrafts(d => ({ ...d, [brand.brandSite]: e.target.value }))
                        }
                      />
                    </Field>
                    <InlineActions>
                      <Button
                        text="Make admin"
                        size="sm"
                        type="button"
                        isDisabled={!adminDraft}
                        onClick={() => saveAdmin(brand.brandSite, true)}
                      />
                      <Button
                        text="Remove admin"
                        theme="secondary"
                        size="sm"
                        type="button"
                        isDisabled={!adminDraft}
                        onClick={() => saveAdmin(brand.brandSite, false)}
                      />
                    </InlineActions>
                  </SubSection>

                  <SubSection>
                    <SubTitle>Stripe account</SubTitle>
                    <FormGrid $cols={3}>
                      <Field label="Secret key">
                        <Input
                          type="password"
                          placeholder="sk_live_… (blank keeps current)"
                          value={secrets.secretKey}
                          onChange={e => setSecret(brand.brandSite, { secretKey: e.target.value })}
                        />
                      </Field>
                      <Field label="Webhook secret">
                        <Input
                          type="password"
                          placeholder="whsec_… (blank keeps current)"
                          value={secrets.webhookSecret}
                          onChange={e =>
                            setSecret(brand.brandSite, { webhookSecret: e.target.value })
                          }
                        />
                      </Field>
                      <Field label="Publishable key">
                        <Input
                          placeholder="pk_live_… (blank keeps current)"
                          value={secrets.publishableKey}
                          onChange={e =>
                            setSecret(brand.brandSite, { publishableKey: e.target.value })
                          }
                        />
                      </Field>
                    </FormGrid>
                    <InlineActions>
                      <Button
                        text="Save Stripe secrets"
                        theme="secondary"
                        size="sm"
                        type="button"
                        onClick={() => saveSecrets(brand.brandSite)}
                      />
                      <Muted $size="xs">Webhook path: /stripe/webhook/{brand.brandSite}</Muted>
                    </InlineActions>
                  </SubSection>
                </SubSections>
              </CardBody>
            </Card>
          );
        })}
      </Stack>
    </PageContainer>
  );
}
